// src/views/Seguridad/Permisos/ListadoPermisos.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

// Componentes reutilizables
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable";
import IconEdit from "../../../components/icons/IconEdit";
import IconTrash from "../../../components/icons/IconTrash";

// Componentes propios de la vista
import FiltroPermisos from "./FiltroPermisos";
import CrearPermisos from "./CrearPermisos";
import EliminarPermiso from "./EliminarPermiso";
import EditarPermiso from "./EditarPermisos";

// Hooks y utilidades
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

// ===== Helpers =====
const mapPermisoDtoToRow = (dto) => ({
  id: dto.id,
  menuNombre: dto.menuNombre ?? "—",
  moduloNombre: dto.moduloNombre ?? "—",
  subModuloNombre: dto.subModuloNombre ?? "—",
  permiso: dto.accion || "—",
});

const isAbort = (e) =>
  e?.name === "AbortError" || /aborted|abort/i.test(e?.message || "");

export default function ListadoPermisos() {
  // ===== Vistas =====
  const [mostrarCrear, setMostrarCrear] = useState(false);
  useEscClose(() => setMostrarCrear(false), mostrarCrear);

  const [mostrarEliminar, setMostrarEliminar] = useState(false);
  useEscClose(() => setMostrarEliminar(false), mostrarEliminar);

  const [mostrarEditor, setMostrarEditor] = useState(false);
  useEscClose(() => setMostrarEditor(false), mostrarEditor);

  const [permisoParaEditar, setPermisoParaEditar] = useState(null);

  // ===== Listado =====
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(30);
  const [refreshTick, setRefreshTick] = useState(0);

  const [filtros, setFiltros] = useState({});
  const hasActiveFilters = (f) =>
    f && Object.values(f).some((v) => (v ?? "") !== "");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const [items, setItems] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => setMostrarFiltros(false), mostrarFiltros);

  // ===== URL =====
  const url = useMemo(() => {
    const backendPage = Math.max(0, pagina - 1);
    const sort = [];

    const base = apiRoutes.permisos.listUrl({
      page: backendPage,
      size: porPagina,
      modulo: filtros.modulo || undefined,
      sort,
    });

    return `${base}&rt=${refreshTick}`;
  }, [pagina, porPagina, filtros, refreshTick]);

  // Reset paginación
  useEffect(() => setPagina(1), [porPagina, filtros]);

  // Cargar datos
  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await appFetchJson(url);
        const mapped = (data?.items || []).map(mapPermisoDtoToRow);
        setItems(mapped);
        setTotalElements(Number(data?.totalElements || 0));
        setTotalPages(Math.max(1, Number(data?.totalPages || 1)));

        const lastUiPage = Math.max(1, Number(data?.totalPages || 1));
        if (pagina > lastUiPage) setPagina(lastUiPage);
      } catch (e) {
        if (!isAbort(e)) {
          console.error("Error cargando permisos:", e);
          setError(e);
          setItems([]);
          setTotalElements(0);
          setTotalPages(1);
        }
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [url]);

  const cambiarPagina = (num) => {
    if (num < 1) num = 1;
    if (num > totalPages) num = totalPages;
    setPagina(num);
  };

  /* ================== FULL SCREEN: Crear permiso ================== */
  if (mostrarCrear) {
    return (
      <CrearPermisos
        onVolver={() => setMostrarCrear(false)}
        onCreated={() => {
          setMostrarCrear(false);
          setPagina(1);
          setRefreshTick((t) => t + 1);
        }}
      />
    );
  }

  /* ================== COLUMNAS PARA LA TABLA ================== */
  const columns = [
    {
      header: "Menú",
      accessor: "menuNombre",
      type: "text",
      width: "150px",
    },
    {
      header: "Módulo",
      accessor: "moduloNombre",
      type: "text",
      width: "150px",
    },
    {
      header: "Sub. Módulo",
      accessor: "subModuloNombre",
      type: "text",
      width: "150px",
    },
    {
      header: "Permiso",
      accessor: "permiso",
      type: "text",
      width: "120px",
    },
    {
      header: "Acción",
      type: "action",
      width: "100px",
      render: (row) => (
        <div className="flex justify-center items-center gap-2">
          <button
            className="p-1 text-gray-500 hover:text-emerald-600"
            title="Editar"
            onClick={() => {
              setPermisoParaEditar(row);
              setMostrarEditor(true);
            }}
          >
            <IconEdit />
          </button>
          <button
            className="p-1 text-gray-500 hover:text-red-600"
            title="Eliminar"
            onClick={() => {
              setPermisoParaEditar(row);
              setMostrarEliminar(true);
            }}
          >
            <IconTrash />
          </button>
        </div>
      ),
    },
  ];

  /* ================== LISTA ================== */
  return (
    <div className="page">
      {/* HEADER con filtros, botón agregar y selector de página */}
      <ListHeader
        title="🔒 Permisos"
        filters={filtros}
        onFilterClick={() => setMostrarFiltros(true)}
        onClearFilters={() => {
          setFiltros({});
          setMostrarFiltros(false);
        }}
        showAdd
        addLabel="Agregar"
        onAddClick={() => setMostrarCrear(true)}
        pageSize={porPagina}
        onPageSizeChange={(size) => setPorPagina(size)}
      />

      {/* PANEL DE FILTROS (drawer) */}
      {mostrarFiltros && (
        <FiltroPermisos
          initial={{ moduloId: filtros.modulo ?? "" }}
          onSubmit={(applied) => {
            const next = {};
            if (applied?.modulo) next.modulo = applied.modulo;
            setFiltros(next);
            setMostrarFiltros(false);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* Indicador de carga / error */}
      {loading && (
        <div className="mt-4 text-sm text-center text-gray-600">
          Cargando permisos…
        </div>
      )}
      {!loading && error && (
        <div className="mt-4 text-sm text-center text-red-600">
          Error al cargar los datos.
        </div>
      )}

      {/* TABLA DINÁMICA */}
      {!loading && !error && (
        <DynamicTable
          size="M" // 5 columnas
          data={items}
          columns={columns}
        />
      )}

      {/* PAGINADOR */}
      <ListFooter
        page={pagina}
        pageSize={porPagina}
        total={totalElements}
        onPageChange={cambiarPagina}
      />

      {/* ========================================================= */}
      {/* =============== MODAL ELIMINAR PERMISO ================= */}
      {/* ========================================================= */}
      {mostrarEliminar && permisoParaEditar && (
        <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[600px] rounded-2xl shadow-2xl overflow-hidden">
            <EliminarPermiso
              initial={{
                id: permisoParaEditar.id,
                moduloNombre: permisoParaEditar.moduloNombre,
                subModuloNombre: permisoParaEditar.subModuloNombre,
                accion: permisoParaEditar.permiso,
                asignadoA: [],
              }}
              onVolver={() => {
                setMostrarEliminar(false);
                setPermisoParaEditar(null);
              }}
              onEliminar={async () => {
                setMostrarEliminar(false);
                setPermisoParaEditar(null);
                setPagina(1);
                setRefreshTick((t) => t + 1);
              }}
            />
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* =============== MODAL EDITAR PERMISO =================== */}
      {/* ========================================================= */}
      {mostrarEditor && permisoParaEditar && (
        <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[650px] rounded-2xl shadow-2xl overflow-hidden">
            <EditarPermiso
              initial={{
                id: permisoParaEditar.id,
                menuNombre: permisoParaEditar.menuNombre,
                moduloNombre: permisoParaEditar.moduloNombre,
                subModuloNombre: permisoParaEditar.subModuloNombre,
                accion: permisoParaEditar.permiso,
              }}
              onClose={() => {
                setMostrarEditor(false);
                setPermisoParaEditar(null);
              }}
              onSaved={() => {
                setMostrarEditor(false);
                setPermisoParaEditar(null);
                setRefreshTick((t) => t + 1);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}